import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';

import { DomSanitizer } from '@angular/platform-browser';
import { Roles } from '@nrwl/rx-models';
import { ScrollBarService } from '@nrwl/scrollbar-service';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { RxRoleGuardDirective } from '@org/rx-directives';
import { RxIconComponent } from '../../rx-icon/rx-icon.component';
import { RxSearchComponent, RxSearchModule } from '@nrwl/rx-search';

@Component({
  selector: 'rx-list-item',
  templateUrl: './rx-list-item.component.html',
  styleUrls: ['./rx-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RxIconComponent,
    MatMenuModule,
    MatCheckboxModule,
    RxRoleGuardDirective,
  ],
})
export class RxListItemComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<boolean> = new Subject<boolean>();

  @Input() indicator: TemplateRef<{ readonly selected: boolean }> | null = null;
  @Input() disableOptimisation = true;
  @Input() id = 'item' + Math.random().toString(36).substring(2);
  @Input() title: string;
  @Input() description: string;
  @Input() trailingText: string;
  @Input() icon: string;
  @Input() image: string;
  @Input() checkbox: boolean;
  @Input() selected: boolean;
  @Input() itemControls: TemplateRef<any>;
  @Input() showDragHandle: boolean;
  @Input() showControls: boolean;
  @Input() showMenuHandle: boolean;
  @Input() showPreviewHandle: boolean;
  @Input() showEditHandle: boolean;
  @Input() showIconCheck: boolean;
  @Input() actionMenu: any;
  @Input() actionMenuContext: any;
  @Input() data: any;
  @Input() disableMenu: boolean;
  @Input() disableEdit: boolean;
  @Input() rejectedRoles: Roles[] = [];
  @Input() customTextClickHandler: boolean;
  @Input() avatarType = 'circle';
  @Output() thumbClicked = new EventEmitter<any>();
  @Output() editClicked = new EventEmitter<any>();
  @Output() contentClicked = new EventEmitter<any>();
  // Checked two way binding
  private _checked: boolean;
  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() preview = new EventEmitter<any>();
  @Input()
  get checked() {
    return this._checked;
  }
  set checked(val) {
    if (this._checked === val) {
      return;
    }
    this._checked = val;
    this.changeDetectorRef.detectChanges();
    // this.checkedChange.emit(val);
  }

  @Input() enableDrag: boolean;
  @Output() enableDragChange = new EventEmitter<boolean>();

  public insideVisibleArea = false;
  private parentVisibleArea: { id: any; rect: any };
  private parentRef: any;

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef,
    private scrollService: ScrollBarService,
    private sanitizer: DomSanitizer,
    private viewContainerRef: ViewContainerRef,
  ) {}

  ngOnInit() {
    if (this.disableOptimisation) {
      this.insideVisibleArea = true;
      return;
    }
    // TODO: WARNING: Optimization related
    // - Show simplified version of element outside visible area.
    // - Instead of passing parent visible rectangle with @Input,
    // - we look for closest RxAccordion component to enable rendering optimization
    let parent = this.viewContainerRef.element.nativeElement;
    while (parent) {
      if (parent.localName === 'rx-accordion') {
        this.parentRef = parent;
        this.setupOptimization(parent);
        break;
      }
      parent = parent.parentElement;
    }

    // Process first time run
    this.render();

    // Process scroll events
    this.scrollService.scrolled.pipe(takeUntil(this.destroyed$)).subscribe((scroll) => {
      if (scroll) {
        this.render(scroll);
      }
    });
  }

  setupOptimization(element) {
    const id = element.getAttribute('data-element-id');
    const rect = element.getBoundingClientRect();
    this.parentVisibleArea = { id: id, rect: rect };
  }

  render(scrollData?: any) {
    // Display host if parent visible area is undefined
    if (!this.parentVisibleArea) {
      this.insideVisibleArea = true;
      return;
    }

    // HACK: getAttribute not available onInit when using optimization in Dialogs
    if (!this.parentVisibleArea.id) {
      this.parentVisibleArea.id = this.parentRef.getAttribute('data-element-id');
    }

    // Do nothing on scroll event with parent visible area id mismatch
    if (scrollData && this.parentVisibleArea.id !== scrollData.id) {
      return;
    }

    // Display SVG placeholder outside visible area
    /**
     * Set setTimeout because of issue when svg renders wrong
     * (calculate wrong top and bottom)
     */
    setTimeout(() => {
      const offset = 100;
      const top = (scrollData || this.parentVisibleArea).rect.top - offset;
      const bottom = (scrollData || this.parentVisibleArea).rect.bottom + offset;

      const selfTop = this.elementRef.nativeElement.getBoundingClientRect().top;
      // if (top < selfTop && bottom > selfTop) {
      if (top < selfTop) {
        this.insideVisibleArea = true;
        if (!this.changeDetectorRef['destroyed']) {
          this.changeDetectorRef.detectChanges();
        }
      } else {
        this.insideVisibleArea = false;
        if (!this.changeDetectorRef['destroyed']) {
          this.changeDetectorRef.detectChanges();
        }
      }
    });
  }

  onEnableDrag(state: boolean) {
    if (this.enableDrag !== state) {
      this.enableDrag = state;
      this.enableDragChange.emit(state);
    }
  }

  onMenuClicked($event) {
    $event.preventDefault();
    $event.stopPropagation();
  }

  onThumbClicked(item) {
    this.thumbClicked.emit(item);
  }

  onEditClicked($event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.editClicked.emit($event);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.destroyed$.unsubscribe();
  }

  onCheckedChange() {
    this.checkedChange.emit(this.checked);
  }

  getHtml(string) {
    return !!string ? this.sanitizer.bypassSecurityTrustHtml(string) : '';
  }

  toggleChecked() {
    // WARNING: Prevent selection prior confirmation (Form Editor -> Discard Changes prompt)
    // TODO: Test other scenarios and remove code below if there no exceptions
    // return;
    if (this.customTextClickHandler) {
      this.contentClicked.emit();
    } else {
      this.checked = !this.checked;
      this.checkedChange.emit(this.checked);
    }
  }

  onPreview(item) {
    this.preview.emit(item);
  }
}
